import { adminClient, selfClient } from './api-client';
import { APP_ROLE_PREFIX, SYSTEM_ROLES } from '../../constants/auth-methods';

// ─── Types ───

/** Keycloak user representation */
export interface KeycloakUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  createdTimestamp: number;
  emailVerified: boolean;
}

/** Full Keycloak user representation including attributes and required actions */
export interface KeycloakUserDetail extends KeycloakUser {
  attributes: Record<string, string[]>;
  requiredActions: string[];
}

/** Keycloak role representation */
export interface KeycloakRole {
  id: string;
  name: string;
  description: string;
}

/** Pending account request (user with their current roles) */
export interface AccountRequest {
  user: KeycloakUser;
  roles: string[];
}

/** User session representation */
export interface KeycloakSession {
  id: string;
  username: string;
  userId: string;
  ipAddress: string;
  start: number;
  lastAccess: number;
  clients: Record<string, string>;
}

/** Credential record from Keycloak */
export interface UserCredential {
  id: string;
  type: string;
  createdDate: number;
}

// ─── User CRUD ───

/** Fetch all users in the realm */
export function fetchUsers(token: string): Promise<KeycloakUser[]> {
  return adminClient(token).get('/users?max=100');
}

/** Fetch a single user's full representation including attributes */
export function fetchUserById(token: string, userId: string): Promise<KeycloakUserDetail> {
  return adminClient(token).get(`/users/${userId}`);
}

/** Create a new user and return the server-assigned ID */
export function createUser(token: string, userData: {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}): Promise<string> {
  return adminClient(token).postForId('/users', {
    username: userData.username,
    email: userData.email,
    firstName: userData.firstName,
    lastName: userData.lastName,
    enabled: true,
    emailVerified: true,
    credentials: [{ type: 'password', value: userData.password, temporary: false }],
  });
}

/** Update a user's attributes */
export function updateUser(token: string, userId: string, data: Partial<KeycloakUser>): Promise<void> {
  return adminClient(token).put(`/users/${userId}`, data);
}

/** Delete a user */
export function deleteUser(token: string, userId: string): Promise<void> {
  return adminClient(token).del(`/users/${userId}`);
}

/**
 * Set or reset a user's password. When `temporary` is true, Keycloak forces
 * the user to change it at next login.
 */
export function resetUserPassword(
  token: string,
  userId: string,
  password: string,
  temporary = false,
): Promise<void> {
  return adminClient(token).put(`/users/${userId}/reset-password`, {
    type: 'password',
    value: password,
    temporary,
  });
}

/**
 * Send the user a reset-password email via Keycloak's execute-actions-email
 * flow (the `UPDATE_PASSWORD` required action). Delivery depends on the realm
 * having SMTP configured.
 */
export function sendPasswordResetEmail(token: string, userId: string): Promise<void> {
  return adminClient(token).put(`/users/${userId}/execute-actions-email`, ['UPDATE_PASSWORD']);
}

/** Enable a user account */
export function enableUser(token: string, userId: string): Promise<void> {
  return adminClient(token).put(`/users/${userId}`, { enabled: true });
}

/** Disable a user account */
export function disableUser(token: string, userId: string): Promise<void> {
  return adminClient(token).put(`/users/${userId}`, { enabled: false });
}

// ─── Roles ───

/** Fetch realm roles assigned to a user */
export function fetchUserRoles(token: string, userId: string): Promise<KeycloakRole[]> {
  return adminClient(token).get(`/users/${userId}/role-mappings/realm`);
}

/** Fetch all available realm roles */
export function fetchRealmRoles(token: string): Promise<KeycloakRole[]> {
  return adminClient(token).get('/roles');
}

/** Assign realm roles to a user */
export async function assignRoles(token: string, userId: string, roles: KeycloakRole[]): Promise<void> {
  await adminClient(token).post(`/users/${userId}/role-mappings/realm`, roles);
}

/** Remove realm roles from a user */
export function removeUserRole(token: string, userId: string, roles: KeycloakRole[]): Promise<void> {
  return adminClient(token).del(`/users/${userId}/role-mappings/realm`, roles);
}

/** Create a realm role */
export async function createRealmRole(token: string, name: string, description?: string): Promise<void> {
  await adminClient(token).post('/roles', { name, description });
}

/** Count users with a specific role */
export async function fetchRoleUserCount(token: string, roleName: string): Promise<number> {
  const users = await adminClient(token).get<KeycloakUser[]>(`/roles/${roleName}/users?max=1000`);
  return users.length;
}

// ─── Account Workflows ───

/** Maps preferred auth methods to Keycloak required action identifiers */
const AUTH_METHOD_ACTIONS: Record<string, string | null> = {
  mfa: 'CONFIGURE_TOTP',
  biometric: 'webauthn-register',
  cac: null,
};

/** Get all pending account requests (enabled users without any app roles) */
export async function fetchPendingRequests(token: string, currentUsername: string): Promise<AccountRequest[]> {
  const users = await fetchUsers(token);
  const requests: AccountRequest[] = [];

  for (const user of users) {
    if (user.username === 'admin' || user.username === currentUsername || !user.enabled) {
      continue;
    }
    const roles = await fetchUserRoles(token, user.id);
    const roleNames = roles.map(r => r.name);
    const hasAppRole = roleNames.some(name => name.startsWith(APP_ROLE_PREFIX));

    if (!hasAppRole) {
      requests.push({
        user,
        roles: roleNames.filter(name => !SYSTEM_ROLES.has(name)),
      });
    }
  }

  return requests;
}

/** Approve a user: enable account and assign requested app roles */
export async function approveUser(token: string, userId: string, roleIds: KeycloakRole[]): Promise<void> {
  await enableUser(token, userId);
  if (roleIds.length > 0) {
    await assignRoles(token, userId, roleIds);
  }
}

/** Deny a user: disable their account */
export function denyUser(token: string, userId: string): Promise<void> {
  return disableUser(token, userId);
}

/** Assign required actions based on the user's preferredAuthMethod attribute */
export async function assignAuthSetupAction(token: string, userId: string): Promise<string | null> {
  const user = await fetchUserById(token, userId);
  const preferredMethod = user.attributes?.preferredAuthMethod?.[0];
  if (!preferredMethod) return null;

  const requiredAction = AUTH_METHOD_ACTIONS[preferredMethod];
  if (!requiredAction) return null;

  await adminClient(token).put(`/users/${userId}`, {
    ...user,
    requiredActions: [requiredAction],
  });
  return requiredAction;
}

// ─── Sessions & Credentials ───

/** Fetch active sessions for a user */
export function fetchUserSessions(token: string, userId: string): Promise<KeycloakSession[]> {
  return adminClient(token).get(`/users/${userId}/sessions`);
}

/** Fetch configured credentials for a user */
export function fetchUserCredentials(token: string, userId: string): Promise<UserCredential[]> {
  return adminClient(token).get(`/users/${userId}/credentials`);
}

/** Delete a specific credential from a user */
export function deleteUserCredential(token: string, userId: string, credentialId: string): Promise<void> {
  return adminClient(token).del(`/users/${userId}/credentials/${credentialId}`);
}

/** Force logout a specific user */
export async function logoutUser(token: string, userId: string): Promise<void> {
  await adminClient(token).post(`/users/${userId}/logout`);
}

// ─── Self-Service ───

/** Fetch the current user's own profile via self-service proxy */
export function fetchSelfProfile(token: string): Promise<KeycloakUserDetail> {
  return selfClient(token).get('/me');
}

/** Fetch the current user's own credentials via self-service proxy */
export function fetchSelfCredentials(token: string): Promise<UserCredential[]> {
  return selfClient(token).get('/me/credentials');
}
